import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import styled from '@emotion/styled';
import { uiTheme as t, Button, EmptyState, Pagination, SearchBox, Checkbox } from 'Components/ui';
import type { PaginationLabels } from 'Components/ui';

export interface SelectableCourse {
  id: string;
  name: string;
  /** Minutos. La duración se formatea aquí para que todas las listas la lean igual. */
  duration?: number | null;
}

export interface SelectCoursesDialogLabels {
  title: string;
  searchPlaceholder: string;
  name: string;
  duration: string;
  selectAll: string;
  selectedCount: string;
  empty: string;
  loading: string;
  cancel: string;
  confirm: string;
  pagination: PaginationLabels;
}

interface SelectCoursesDialogProps {
  open: boolean;
  labels: SelectCoursesDialogLabels;
  courses: SelectableCourse[];
  loading?: boolean;
  /** Bloquea los botones mientras el guardado está en vuelo. */
  submitting?: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  /** Paginación en base 1; quien la use adapta si su fuente cuenta desde cero. */
  page: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  /** Sin este callback el diálogo no ofrece el selector de tamaño. */
  onPageSizeChange?: (pageSize: number) => void;
  onClose: () => void;
  /** Devuelve true si la operación salió bien; en falso el diálogo sigue abierto. */
  onConfirm: (courseIds: string[]) => Promise<boolean>;
}

const GRID = '38px 1fr 120px';

const Content = styled.div({
  padding: 24,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const Head = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
});

const Title = styled.div({
  fontFamily: t.font,
  fontSize: 18,
  fontWeight: 600,
  color: t.color.title,
});

const Count = styled.span({
  padding: '5px 10px',
  borderRadius: 999,
  background: t.color.countBg,
  color: t.color.countText,
  fontFamily: t.font,
  fontSize: 12.5,
  fontWeight: 600,
  whiteSpace: 'nowrap',
});

const Surface = styled.div({
  border: `1px solid ${t.color.border}`,
  borderRadius: t.radius.lg,
  overflow: 'hidden',
});

const HeadRow = styled.div({
  display: 'grid',
  gridTemplateColumns: GRID,
  alignItems: 'center',
  gap: 16,
  padding: '12px 20px',
  background: t.color.tableHeadBg,
  borderBottom: `1px solid ${t.color.rowBorder}`,
});

const HeadCell = styled.span({
  fontFamily: t.font,
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: '0.6px',
  color: t.color.tableHeadText,
});

const BodyRow = styled.label({
  display: 'grid',
  gridTemplateColumns: GRID,
  alignItems: 'center',
  gap: 16,
  padding: '13px 20px',
  borderTop: `1px solid ${t.color.rowBorder}`,
  cursor: 'pointer',
  '&:hover': { background: t.color.rowHoverBg },
});

const Name = styled.div({
  fontFamily: t.font,
  fontSize: 14,
  fontWeight: 600,
  color: t.color.title,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const Cell = styled.div({
  fontFamily: t.font,
  fontSize: 13.5,
  color: t.color.text,
});

const Scroll = styled.div({
  maxHeight: 360,
  overflowY: 'auto',
});

const LoadingState = styled.div({
  padding: '48px 20px',
  textAlign: 'center',
  fontFamily: t.font,
  fontSize: 14,
  color: t.color.muted,
});

const Actions = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 12,
});

/** Los minutos crudos no dicen nada de un vistazo; "1h 30m" sí. */
const formatDuration = (minutes?: number | null) => {
  if (!minutes) return '—';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours > 0 ? `${hours}h ${rest}m` : `${rest}m`;
};

/**
 * Organismo: diálogo para elegir cursos de un catálogo y aplicarlos en lote.
 *
 * No sabe a quién se los asigna: recibe la página de cursos ya pedida y
 * devuelve los ids elegidos. Sirve igual para puestos, departamentos o
 * cualquier otro destino que aparezca después.
 *
 * La selección vive aquí y no en las filas visibles: con paginación en el
 * servidor, marcarla sobre lo que se ve en pantalla perdería lo elegido en la
 * página anterior al pasar a la siguiente.
 */
const SelectCoursesDialog = ({
  open,
  labels,
  courses,
  loading = false,
  submitting = false,
  search,
  onSearchChange,
  page,
  pageSize,
  totalPages,
  totalItems,
  onPageChange,
  onPageSizeChange,
  onClose,
  onConfirm,
}: SelectCoursesDialogProps) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const allSelected = courses.length > 0 && courses.every(course => selected.has(course.id));

  const toggle = (courseId: string) => {
    setSelected(previous => {
      const next = new Set(previous);
      if (next.has(courseId)) next.delete(courseId);
      else next.add(courseId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(previous => {
      const next = new Set(previous);
      courses.forEach(course => (allSelected ? next.delete(course.id) : next.add(course.id)));
      return next;
    });
  };

  const close = () => {
    setSelected(new Set());
    onClose();
  };

  const confirm = async () => {
    // `Array.from` y no spread: el target de compilación no itera Sets.
    const ok = await onConfirm(Array.from(selected));
    if (!ok) return;

    setSelected(new Set());
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      PaperProps={{
        sx: { width: 620, maxWidth: '92vw', borderRadius: '16px', boxShadow: '0 20px 50px rgba(16, 24, 40, 0.25)' },
      }}
    >
      <Content>
        <Head>
          <Title>{labels.title}</Title>
          {selected.size > 0 ? (
            <Count>
              {selected.size} {labels.selectedCount}
            </Count>
          ) : null}
        </Head>

        <SearchBox value={search} placeholder={labels.searchPlaceholder} onChange={onSearchChange} />

        <Surface>
          <HeadRow>
            <Checkbox checked={allSelected} onChange={toggleAll} aria-label={labels.selectAll} />
            <HeadCell>{labels.name}</HeadCell>
            <HeadCell>{labels.duration}</HeadCell>
          </HeadRow>

          {loading ? <LoadingState>{labels.loading}</LoadingState> : null}

          {!loading && courses.length === 0 ? <EmptyState message={labels.empty} /> : null}

          {!loading ? (
            <Scroll>
              {courses.map(course => (
                <BodyRow key={course.id}>
                  <Checkbox
                    checked={selected.has(course.id)}
                    onChange={() => toggle(course.id)}
                    aria-label={course.name}
                  />
                  <Name>{course.name}</Name>
                  <Cell>{formatDuration(course.duration)}</Cell>
                </BodyRow>
              ))}
            </Scroll>
          ) : null}
        </Surface>

        {totalItems > 0 ? (
          <Pagination
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            totalItems={totalItems}
            onChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            labels={labels.pagination}
          />
        ) : null}

        <Actions>
          <Button type="button" variant="secondary" onClick={close} disabled={submitting}>
            {labels.cancel}
          </Button>
          <Button type="button" variant="primary" onClick={confirm} disabled={submitting || selected.size === 0}>
            {labels.confirm}
          </Button>
        </Actions>
      </Content>
    </Dialog>
  );
};

export default SelectCoursesDialog;
